import {
  createTypes,
  completeTypes,
  withPostSuccess,
  withPostFailure,
  withSuccess,
  withPreFetch
} from 'redux-recompose';

import OrderService from '~services/Order/service';
import ModalActions from '~redux/Modal/actions';
import { arrayHasLength, removeDuplicates, removeItemFromArray } from '~utils/array';
import { MODALS } from '~redux/Modal/constants';
import { DEFAULT_ITEM, PREPARING, SIDEBAR_ITEMS } from '~app/components/SideBar/constants';
import { ORDER_STATUS } from '~constants/orders';
import { isPrinterManagerFeatureEnabled } from '~constants/features';

import { orderMapper } from './mappers';
import { shouldRefetchOnPusherStatus } from './pusherRefetch';

export const actions = createTypes(
  completeTypes(
    [
      'GET_ORDERS',
      'SET_ORDERS_FILTER',
      'UPDATE_ORDER',
      'UPDATE_ORDER_BY_PUSHER',
      'UPDATE_ORDER_DELIVERY_TYPE',
      'SET_ACTIVE_TAB',
      'GET_PEYA_CANCELLED_ORDER',
      'SET_NEW_ORDERS',
      'SET_CANCELLING_ORDER',
      'GET_QUANTITY_ORDERS',
      'CHECK_PENDING_ORDERS',
      'UPDATE_ORDER_FROM_LIST',
      'ADD_ORDER_TO_QUEUE',
      'GET_ORDER_TO_PRINT',
      'ADD_ORDER_ID_TO_STACK',
      'REMOVE_ORDER_ID_FROM_STACK',
      'RESET_ORDER_ID_STACK'
    ],
    ['SET_NULL_PEYA_CANCELLED_ORDER', 'PRINT', 'REMOVE_PRINT_QUEUE']
  ),
  '@@ORDER'
);

export const targets = {
  ORDERS: 'orders',
  ORDERS_FILTER: 'ordersFilter',
  UPDATE_ORDER: 'updateOrder',
  UPDATE_ORDER_DELIVERY_TYPE: 'updateOrderDeliveryType',
  ACTIVE_TAB: 'activeTab',
  PEYA_CANCELLED_ORDER: 'peyaCancelledOrder',
  SET_NULL_PEYA_CANCELLED_ORDER: 'setNullPeyaCancelledOrder',
  NEW_ORDERS: 'newOrders',
  CANCELLING_ORDER: 'cancellingOrder',
  QUANTITY_ORDERS: 'quantityOrders',
  PENDING_ORDERS: 'pendingOrders',
  PRINT: 'print',
  ORDER_TO_PRINT: 'orderToPrint',
  ORDER_QUEUE: 'orderQueue',
  ORDER_ID_STACK: 'orderIdStack'
};

const actionCreators = {
  getOrders: (storeId, ordersFilter, page) => (dispatch, getState) => {
    const state = getState();
    const { ordersFilter: stateOrderFilter } = state.order;

    const filters = ordersFilter || stateOrderFilter;

    return dispatch({
      type: actions.GET_ORDERS,
      target: targets.ORDERS,
      payload: {
        storeId,
        ...(!filters.includes(ORDER_STATUS.PRODUCTS) && {
          ordersFilter: filters
        }),
        page
      },
      service: OrderService.getOrders,
      successSelector: ({ data }) => {
        return {
          list: orderMapper(data.page, ...(!filters.includes(ORDER_STATUS.PRODUCTS) && filters)),
          currentPage: data.currentPage,
          nextPage: data.nextPage,
          previousPage: data.previousPage,
          totalPages: data.totalPages,
          totalCount: data.totalCount
        };
      },
      injections: [
        withPostSuccess((dispatch) => {
          return dispatch(actionCreators.getQuantityOrders(storeId));
        })
      ]
    });
  },
  setOrdersFilter: (ordersFilter) => ({
    type: actions.SET_ORDERS_FILTER,
    target: targets.ORDERS_FILTER,
    payload: ordersFilter
  }),
  setActiveTab: (status) => ({
    type: actions.SET_ACTIVE_TAB,
    target: targets.ACTIVE_TAB,
    payload: status
  }),
  setNewOrders: (orders) => ({
    type: actions.SET_NEW_ORDERS,
    target: targets.NEW_ORDERS,
    payload: removeDuplicates(orders)
  }),
  addOrderToQueue:
    ({ orderId, storeId }, cancelToken) =>
    (dispatch, getState) => {
      const state = getState();
      const { storeSelected } = state.core;
      const { activeTab, orderIdStack } = state.order;

      if (
        activeTab?.toLowerCase() === ORDER_STATUS.REJECTED_ORDER_MANAGER ||
        activeTab?.toLowerCase() === ORDER_STATUS.ORDER_TAKER
      ) {
        return;
      }

      if (storeId === storeSelected && !orderIdStack.includes(orderId)) {
        return dispatch({
          type: actions.ADD_ORDER_TO_QUEUE,
          target: targets.ORDER_QUEUE,
          payload: {
            id: orderId,
            storeId,
            cancelToken
          },
          service: OrderService.getOrderById,
          successSelector: ({ data }) => data.id,
          injections: [
            withPreFetch((dispatch) => {
              dispatch(actionCreators.addOrderIdToStack(orderId));
            }),
            withSuccess((_, response) => {
              const updatedState = getState();
              const { newOrders } = updatedState.order;
              dispatch(actionCreators.setNewOrders(newOrders.concat(response.data)));
            })
          ]
        });
      }
    },
  removeOrderFromQueue: (orderId, isAutoAcceptEnabled) => (dispatch, getState) => {
    const state = getState();
    const { newOrders } = state.order;
    const { storeSelected } = state.core;
    const open = state.modal[`${MODALS.ANNUL_ORDER}ModalIsOpen`];
    const newQueue = removeItemFromArray(newOrders, orderId, 'id');

    dispatch(actionCreators.setNewOrders(newQueue));
    dispatch(actionCreators.removeOrderIdFromStack(orderId));
    if (!arrayHasLength(newQueue) && !open) {
      const newOrdersFilter = SIDEBAR_ITEMS[isAutoAcceptEnabled ? 'PREPARING' : 'CREATED'].status;
      dispatch(ModalActions.closeModal(MODALS.NEW_ORDERS));
      dispatch(actionCreators.setActiveTab(isAutoAcceptEnabled ? PREPARING : DEFAULT_ITEM));
      dispatch(actionCreators.setOrdersFilter(newOrdersFilter));
      dispatch(actionCreators.getOrders(storeSelected, newOrdersFilter));
    }
  },
  setCancellingOrder: (order) => ({
    type: actions.SET_CANCELLING_ORDER,
    target: targets.CANCELLING_ORDER,
    payload: order
  }),
  updateOrderStatus:
    (status, orderId, storeSelected, onPostSuccess, shouldCheckAutoAcceptOrder) => (dispatch, getState) => {
      const state = getState();
      const { autoAcceptOrder } = state?.stores?.storeById || {};
      const { activeTab, newOrders } = state?.order || {};
      const isNewOrdersModalOpen = state.modal[`${MODALS.NEW_ORDERS}ModalIsOpen`];

      if (
        (!autoAcceptOrder && shouldCheckAutoAcceptOrder) ||
        (shouldCheckAutoAcceptOrder && (isNewOrdersModalOpen || arrayHasLength(newOrders))) ||
        activeTab?.toLowerCase() === ORDER_STATUS.REJECTED_ORDER_MANAGER ||
        activeTab?.toLowerCase() === ORDER_STATUS.ORDER_TAKER
      ) {
        return;
      }

      return dispatch({
        type: actions.UPDATE_ORDER,
        target: targets.UPDATE_ORDER,
        payload: { status, orderId, storeSelected },
        service: OrderService.updateOrderStatus,
        injections: [
          withPostSuccess((dispatch, response, state) => {
            if (onPostSuccess) {
              onPostSuccess();
            }
            if (status === ORDER_STATUS.PREPARING) {
              dispatch(
                actionCreators.addOrderToPrintQueue({
                  ...response.data,
                  store: state.stores.storeById
                })
              );
            }
          })
        ]
      });
    },
  batchUpdateOrderStatus: (orderIds, state, storeSelected, onSuccess) => (dispatch) => {
    return Promise.all(
      orderIds.map((orderId) => dispatch(actionCreators.updateOrderStatus(state, orderId, storeSelected)))
    ).then(onSuccess);
  },
  updateOrderDeliveryType: ({ id, storeId }, estimationId, [resolve, reject]) => ({
    type: actions.UPDATE_ORDER_DELIVERY_TYPE,
    payload: { orderId: id, storeId, estimationId: estimationId },
    service: OrderService.updateOrderDeliveryType,
    target: targets.UPDATE_ORDER_DELIVERY_TYPE,
    successSelector: ({ data }) => resolve(data),
    failureSelector: ({ data }) => reject(data)
  }),
  updateOrderByPusher: (order) => (dispatch, getState) => {
    const state = getState();
    const isNewOrdersModalOpen = state.modal[`${MODALS.NEW_ORDERS}ModalIsOpen`];
    const { newOrders, orders, ordersFilter, ordersLoading } = state.order || {};
    const hasQueuedNewOrders = isNewOrdersModalOpen || arrayHasLength(newOrders || []);

    // Applied even with the modal open: dropping it lost the auto-accept confirmations for good.
    dispatch({
      type: actions.UPDATE_ORDER_BY_PUSHER,
      payload: order,
      target: targets.ORDERS
    });

    if (
      shouldRefetchOnPusherStatus({
        order,
        list: orders?.list,
        ordersFilter,
        ordersLoading,
        hasQueuedNewOrders
      })
    ) {
      dispatch(actionCreators.getOrders(state.core.storeSelected, ordersFilter, orders.currentPage || 1));
    }
  },
  getPeyaCancelledOrder: (storeId, id, onSuccess) => (dispatch, getState) => {
    const state = getState();
    const { activeTab } = state?.order || {};

    if (activeTab?.toLowerCase() === ORDER_STATUS.REJECTED_ORDER_MANAGER) {
      return;
    }

    return dispatch({
      type: actions.GET_PEYA_CANCELLED_ORDER,
      target: targets.PEYA_CANCELLED_ORDER,
      payload: {
        storeId,
        id
      },
      service: OrderService.getOrderById,
      injections: [
        withPostSuccess((_, response) => {
          if (onSuccess) {
            onSuccess(response.data);
          }
        }),
        withPostFailure((dispatch) => {
          dispatch(actionCreators.setNullPeyaCancelledOrder());
        })
      ]
    });
  },
  setNullPeyaCancelledOrder: () => ({
    type: actions.SET_NULL_PEYA_CANCELLED_ORDER,
    target: targets.PEYA_CANCELLED_ORDER,
    payload: null
  }),
  getQuantityOrders: (storeId) => ({
    type: actions.GET_QUANTITY_ORDERS,
    target: targets.QUANTITY_ORDERS,
    payload: { storeId },
    service: OrderService.getQuantityOrders
  }),
  updateOrderFromList:
    ({ orderId, data, keyToUpdate }) =>
    (dispatch, getState) => {
      const state = getState();
      const list = state.order.orders.list;
      const newList = [...list];
      const orderIndex = list.findIndex((order) => order.id === orderId);
      newList[orderIndex] = { ...list[orderIndex], [keyToUpdate]: data };

      dispatch({
        type: actions.UPDATE_ORDER_FROM_LIST,
        target: targets.ORDERS,
        payload: { newList }
      });
    },
  enqueueOrderPrintById: (orderId, storeId) => (dispatch, getState) => {
    const { print, printedOrderIds } = getState().order;
    const isAlreadyHandled = printedOrderIds.includes(orderId) || print.some((order) => order.id === orderId);

    if (!isPrinterManagerFeatureEnabled || isAlreadyHandled) {
      return;
    }

    return dispatch({
      type: actions.GET_ORDER_TO_PRINT,
      target: targets.ORDER_TO_PRINT,
      payload: { storeId, id: orderId },
      service: OrderService.getOrderById,
      injections: [
        withPostSuccess((dispatch, response, state) => {
          // The selected store can change while the request is in flight; storeById follows
          // the selection, so printing now would stamp another store's data on this command.
          if (state.core.storeSelected !== storeId) {
            return;
          }
          dispatch(
            actionCreators.addOrderToPrintQueue({
              ...response.data,
              store: state.stores.storeById
            })
          );
        })
      ]
    });
  },
  addOrderToPrintQueue: (order) => (dispatch) => {
    if (isPrinterManagerFeatureEnabled) {
      dispatch({
        type: actions.PRINT,
        target: targets.PRINT,
        payload: order
      });
    }
  },
  removeOrderPrintQueue: (orderId) => ({
    type: actions.REMOVE_PRINT_QUEUE,
    target: targets.PRINT,
    payload: orderId
  }),
  addOrderIdToStack: (orderId) => ({
    type: actions.ADD_ORDER_ID_TO_STACK,
    target: targets.ORDER_ID_STACK,
    payload: orderId
  }),
  removeOrderIdFromStack: (orderId) => ({
    type: actions.REMOVE_ORDER_ID_FROM_STACK,
    target: targets.ORDER_ID_STACK,
    payload: orderId
  }),
  resetOrderIdStack: () => ({
    type: actions.RESET_ORDER_ID_STACK,
    target: targets.ORDER_ID_STACK
  })
};

export default actionCreators;
